import { forwardRef, useCallback, useState, useRef } from 'react';

const MemeCanvas = forwardRef(({ 
  image, 
  imageSize, 
  textLayers, 
  selectedLayerId, 
  onSelectLayer, 
  onUpdateLayer 
}, ref) => {
  const [dragging, setDragging] = useState(null);
  const dragStart = useRef({ x: 0, y: 0, layerX: 0, layerY: 0 });
  const canvasContainerRef = useRef(null);

  const handleMouseDown = useCallback((e, layer) => {
    e.preventDefault();
    e.stopPropagation();
    onSelectLayer(layer.id);
    setDragging(layer.id);
    
    const rect = canvasContainerRef.current?.getBoundingClientRect();
    if (rect) {
      dragStart.current = {
        x: e.clientX,
        y: e.clientY,
        layerX: layer.x,
        layerY: layer.y
      };
    }
  }, [onSelectLayer]);

  const handleMouseMove = useCallback((e) => {
    if (!dragging || !canvasContainerRef.current) return;
    
    const deltaX = e.clientX - dragStart.current.x;
    const deltaY = e.clientY - dragStart.current.y;
    
    // Convert delta to percentage
    const deltaXPercent = (deltaX / imageSize.width) * 100;
    const deltaYPercent = (deltaY / imageSize.height) * 100;
    
    const newX = Math.max(0, Math.min(100, dragStart.current.layerX + deltaXPercent));
    const newY = Math.max(0, Math.min(100, dragStart.current.layerY + deltaYPercent));
    
    onUpdateLayer(dragging, { x: newX, y: newY });
  }, [dragging, imageSize, onUpdateLayer]);

  const handleMouseUp = useCallback(() => {
    setDragging(null);
  }, []);

  const getTextStyle = (layer) => {
    const style = {
      fontFamily: layer.fontFamily === 'Oswald' ? "'Oswald', sans-serif" : 
                  layer.fontFamily === 'Caveat' ? "'Caveat', cursive" :
                  layer.fontFamily === 'Comic Neue' ? "'Comic Neue', cursive" :
                  layer.fontFamily === 'Inter' ? "'Inter', sans-serif" :
                  `"${layer.fontFamily}", sans-serif`,
      fontSize: `${layer.fontSize}px`,
      color: layer.color,
      fontWeight: layer.bold ? 'bold' : 'normal',
      fontStyle: layer.italic ? 'italic' : 'normal',
      textAlign: layer.align,
      WebkitTextStroke: layer.strokeWidth > 0 ? `${layer.strokeWidth}px ${layer.strokeColor}` : 'none',
      textShadow: layer.shadowBlur > 0 ? `2px 2px ${layer.shadowBlur}px ${layer.shadowColor}` : 'none',
      whiteSpace: 'nowrap',
      userSelect: 'none',
      cursor: 'move',
    };
    return style;
  };

  if (!image) {
    return (
      <div 
        className="w-[600px] h-[400px] bg-[#1a1a1a] rounded-xl border-2 border-dashed border-[#27272a] flex items-center justify-center"
        data-testid="empty-canvas"
      >
        <p className="text-[#a1a1aa]">Select an image or template to start</p>
      </div>
    );
  }

  return (
    <div
      ref={(node) => {
        canvasContainerRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      className="canvas-wrapper relative"
      style={{ width: imageSize.width, height: imageSize.height }}
      data-testid="meme-canvas"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* Background Image */}
      <img
        src={image}
        alt="Meme"
        crossOrigin="anonymous"
        className="w-full h-full object-cover"
        draggable={false}
      />

      {/* Text Layers */}
      {textLayers.map((layer) => {
        const isSelected = layer.id === selectedLayerId;

        return (
          <div
            key={layer.id}
            className={`absolute draggable-text ${isSelected ? 'ring-2 ring-[#bef264] ring-offset-2 ring-offset-transparent rounded' : ''}`}
            style={{
              ...getTextStyle(layer),
              left: `${layer.x}%`,
              top: `${layer.y}%`,
              transform: `translateX(-${layer.align === 'center' ? '50%' : layer.align === 'right' ? '100%' : '0'})`,
            }}
            onMouseDown={(e) => handleMouseDown(e, layer)}
            data-testid={`text-layer-${layer.id}`}
          >
            {layer.uppercase ? layer.text.toUpperCase() : layer.text}
          </div>
        );
      })}
    </div>
  );
});

MemeCanvas.displayName = 'MemeCanvas';

export default MemeCanvas;
